export interface TipCurrency {
  currency: string;
  minor_unit_exponent?: number;
  minimum: number;
  maximum: number;
  presets: number[];
  default_amount: number;
}

export const isTipCurrencyCode = (value: unknown): value is string =>
  typeof value === "string" && /^[a-z]{3}$/.test(value);

export const tipScale = (row?: Pick<TipCurrency, "minor_unit_exponent">) =>
  10 ** (row?.minor_unit_exponent ?? 2);

export function isTipCurrencies(value: unknown): value is TipCurrency[] {
  if (!Array.isArray(value) || value.length > 4) return false;
  const seen = new Set<string>();
  return value.every((row) => {
    if (
      !row ||
      !isTipCurrencyCode(row.currency) ||
      (row.minor_unit_exponent !== undefined &&
        ![0, 1, 2, 3].includes(row.minor_unit_exponent)) ||
      seen.has(row.currency)
    )
      return false;
    seen.add(row.currency);
    return (
      Number.isSafeInteger(row.minimum) &&
      Number.isSafeInteger(row.maximum) &&
      row.minimum > 0 &&
      row.minimum <= row.maximum &&
      row.maximum <= 99999999 &&
      Array.isArray(row.presets) &&
      row.presets.length === 3 &&
      Number.isSafeInteger(row.default_amount) &&
      row.presets.includes(row.default_amount) &&
      row.presets.every(
        (n: unknown, i: number) =>
          Number.isSafeInteger(n) &&
          (n as number) >= row.minimum &&
          (n as number) <= row.maximum &&
          (n as number) % tipScale(row) === 0 &&
          (i === 0 || (n as number) > row.presets[i - 1]),
      )
    );
  });
}

export interface TipStatus {
  enabled: boolean;
  livemode: boolean;
  city_id: number;
  settings_version: number;
  default_currency: string;
  currencies: TipCurrency[];
}

export function isTipStatus(value: unknown): value is TipStatus {
  if (!value || typeof value !== "object") return false;
  const row = value as TipStatus;
  return (
    typeof row.enabled === "boolean" &&
    typeof row.livemode === "boolean" &&
    Number.isSafeInteger(row.city_id) &&
    row.city_id > 0 &&
    Number.isSafeInteger(row.settings_version) &&
    row.settings_version > 0 &&
    isTipCurrencyCode(row.default_currency) &&
    isTipCurrencies(row.currencies) &&
    (row.enabled
      ? row.currencies.some((c) => c.currency === row.default_currency)
      : row.currencies.length === 0)
  );
}

// Tourists enter whole currency units only; the API still receives minor units.
// Do not round decimals or strip separators into a different payment amount.
export function tipMinorUnits(
  text: string,
  row?: Pick<TipCurrency, "minor_unit_exponent">,
): number | null {
  if (!/^\d{1,8}$/.test(text)) return null;
  const amount = Number(text) * tipScale(row);
  return Number.isSafeInteger(amount) && amount <= 99999999 ? amount : null;
}
